import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useScrollBehavior } from './useScrollBehavior.js';
import './faq_module.css';

const FAQModule = ({ renderAnimatedLettersByWord }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.25 });

  useScrollBehavior();

  const [openIndex, setOpenIndex] = useState(0);

  const faqItems = [
    {
      question: 'What kind of businesses is this best for?',
      answer:
        'This works best for service businesses, local businesses, and companies that need a stronger website, SEO support, lead capture, or a more structured digital presence. It is especially useful when the site needs to do more than just look good.'
    },
    {
      question: 'Do you only build websites, or do you handle strategy too?',
      answer:
        'We handle more than visual design. That includes website structure, messaging direction, SEO support, conversion-focused layout decisions, and digital strategy that helps the site support actual business goals.'
    },
    {
      question: 'Can you improve an existing site instead of rebuilding everything?',
      answer:
        'Yes. In some cases, improving an existing site makes more sense than starting over. That depends on the current structure, code quality, performance, and whether the site can realistically support the changes you need.'
    },
    {
      question: 'Do you offer ongoing support after launch?',
      answer:
        'Yes. Ongoing support can include updates, refinements, technical fixes, content changes, SEO work, and continued optimization. That is often the better fit when the website needs to evolve over time.'
    },
    {
      question: 'Can you build AI or chatbot features into a website?',
      answer:
        'Yes. AI features can include chatbot workflows, intake systems, lead qualification, content support tools, and other structured automation features. The goal is to make them useful, not just decorative.'
    },
    {
      question: 'How do I know what solution I actually need?',
      answer:
        'That depends on the current state of the business, the site, and the actual bottleneck. Some businesses need a better website. Some need better SEO. Some need stronger conversion structure. Some need ongoing support instead of a one-time build. The right solution starts with identifying the real problem first.'
    }
  ];

  const container = {
    hidden: { opacity: 0, y: 22 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: 'easeOut',
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: 'easeOut' }
    }
  };

  return (
    <div id="faqmod_wrap" ref={ref}>
      {/* LEFT */}
      <motion.div
        id="faqmod_left"
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={container}
      >
        <motion.div id="faqmod_kicker" variants={item}>
          Clear Answers • Better Decisions • Less Friction • Stronger Conversion
        </motion.div>

        <motion.h1 id="faqmod_h1" variants={item}>
          {renderAnimatedLettersByWord(
            'Answers to common questions before you start the project.',
            inView
          )}
        </motion.h1>

        <motion.p id="faqmod_p" variants={item}>
          A stronger website project starts with clarity. This section answers common questions
          about process, support, strategy, and what kind of work actually makes sense for a
          business that wants better digital performance.
        </motion.p>

        <motion.div id="faqmod_ctaRow" variants={item}>
          <Link to="/contact2" id="faqmod_ctaPrimary">
            Ask a Specific Question
          </Link>

          <div id="faqmod_ctaNote">
            <div className="faqmod_noteTop">Straight answers</div>
            <div className="faqmod_noteBottom">
              Strategy • Design • Development • SEO • AI integration
            </div>
          </div>
        </motion.div>

        <div id="faqmod_visual"></div>
      </motion.div>

      {/* RIGHT */}
      <motion.div
        id="faqmod_right"
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={container}
      >
        {faqItems.map((itemData, index) => {
          const isOpen = openIndex === index;

          return (
            <motion.div
              className={`faqmod_item ${isOpen ? 'open' : ''}`}
              variants={item}
              key={index}
            >
              <button
                type="button"
                className="faqmod_questionRow"
                onClick={() => setOpenIndex(isOpen ? -1 : index)}
              >
                <div className="faqmod_questionText">{itemData.question}</div>
                <div className="faqmod_plus">{isOpen ? '−' : '+'}</div>
              </button>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    className="faqmod_answerWrap"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: 'easeInOut' }}
                  >
                    <div className="faqmod_answer">
                      {itemData.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default FAQModule;